/**
 * Conditional Edge Parser
 * 
 * Parses JavaScript conditional functions to extract possible return values (node IDs)
 * Uses Acorn for AST parsing - safe, reliable, and battle-tested
 */

import * as acorn from 'acorn';

/**
 * Parse a conditional function and extract return values with their conditions
 * 
 * @param {string} functionCode - JavaScript function code
 * @returns {{ success: boolean, branches: Array<{condition: string, target: string}>, error?: string }}
 */
export function parseConditionalFunction(functionCode) {
  try {
    const ast = acorn.parse(functionCode, {
      ecmaVersion: 2022,
      sourceType: 'module',
      ranges: true
    });

    const branches = [];
    const sourceCode = functionCode;

    // Find the function body
    let functionBody = null;
    if (ast.body[0]?.type === 'FunctionDeclaration') {
      functionBody = ast.body[0].body;
    } else if (ast.body[0]?.type === 'ExpressionStatement' && 
               ast.body[0].expression.type === 'ArrowFunctionExpression') {
      functionBody = ast.body[0].expression.body;
    }

    if (!functionBody) {
      return { success: false, branches: [], error: 'Could not find function body' };
    }

    // Walk through if/else if/else statements
    function processIfStatement(node, isElse = false) {
      if (node.type === 'IfStatement') {
        // Get condition text using range
        const conditionText = isElse ? null : (node.test.range ? sourceCode.slice(node.test.range[0], node.test.range[1]) : null);
        
        // Check consequent for return
        const consequentReturn = findReturnValue(node.consequent);
        if (consequentReturn) {
          branches.push({
            condition: conditionText || 'else',
            target: consequentReturn
          });
        }

        // Process alternate (else if or else)
        if (node.alternate) {
          if (node.alternate.type === 'IfStatement') {
            // else if
            processIfStatement(node.alternate, false);
          } else {
            // else block
            const alternateReturn = findReturnValue(node.alternate);
            if (alternateReturn) {
              branches.push({
                condition: 'else',
                target: alternateReturn
              });
            }
          }
        }
      }
    }

    function findReturnValue(node) {
      if (node.type === 'ReturnStatement' && node.argument) {
        return extractValue(node.argument);
      }
      if (node.type === 'BlockStatement') {
        for (const stmt of node.body) {
          const val = findReturnValue(stmt);
          if (val) return val;
        }
      }
      return null;
    }

    function extractValue(node) {
      if (node.type === 'Literal') {
        return String(node.value);
      }
      if (node.type === 'TemplateLiteral' && node.quasis.length === 1) {
        return node.quasis[0].value.cooked;
      }
      return null;
    }

    // Process statements in function body
    for (const stmt of functionBody.body) {
      if (stmt.type === 'IfStatement') {
        processIfStatement(stmt);
      } else if (stmt.type === 'ReturnStatement' && stmt.argument) {
        // Standalone return (fallback)
        const val = extractValue(stmt.argument);
        if (val) {
          branches.push({ condition: 'default', target: val });
        }
      }
    }

    // Deduplicate targets (keep first occurrence of each)
    const seen = new Set();
    const uniqueBranches = branches.filter(b => {
      if (seen.has(b.target)) return false;
      seen.add(b.target);
      return true;
    });

    return {
      success: true,
      branches: uniqueBranches,
      outputs: uniqueBranches.map(b => b.target)
    };

  } catch (error) {
    return {
      success: false,
      branches: [],
      outputs: [],
      error: error.message
    };
  }
}

/**
 * Validate that a conditional function has the correct signature
 * Expected: (state) => string or function(state) { return string; }
 * 
 * @param {string} functionCode - JavaScript function code
 * @returns {{ valid: boolean, error?: string }}
 */
export function validateConditionalFunction(functionCode) {
  try {
    const ast = acorn.parse(functionCode, {
      ecmaVersion: 2022,
      sourceType: 'module'
    });

    // Should have exactly one top-level declaration
    if (ast.body.length !== 1) {
      return {
        valid: false,
        error: 'Function should contain only one function declaration or expression'
      };
    }

    const node = ast.body[0];

    // Check if it's a function declaration
    if (node.type === 'FunctionDeclaration') {
      if (node.params.length !== 1) {
        return {
          valid: false,
          error: 'Function must accept exactly one parameter (state)'
        };
      }
      return { valid: true };
    }

    // Check if it's an arrow function expression
    if (node.type === 'ExpressionStatement' && 
        node.expression.type === 'ArrowFunctionExpression') {
      if (node.expression.params.length !== 1) {
        return {
          valid: false,
          error: 'Function must accept exactly one parameter (state)'
        };
      }
      return { valid: true };
    }

    return {
      valid: false,
      error: 'Code must be a function declaration or arrow function'
    };

  } catch (error) {
    return {
      valid: false,
      error: `Syntax error: ${error.message}`
    };
  }
}

/**
 * Generate a default conditional function template
 * 
 * @param {string[]} possibleOutputs - Array of node IDs that can be returned
 * @param {string} sourceNodeId - ID of the source node (for context-aware templates)
 * @param {string} precedingNodeId - ID of the node that feeds into this decision
 * @returns {string} - JavaScript function code
 */
export function generateConditionalTemplate(possibleOutputs = ['node_a', 'node_b'], sourceNodeId = null, precedingNodeId = null) {
  // Special template for analyze_ticket decision (matches backend logic)
  if (sourceNodeId === 'analyze_ticket_decision' || precedingNodeId === 'analyze_ticket') {
    return `function route(state) {
  const validation = state.validation;

  if (!validation?.canProceed) {
    return 'finalize';
  }

  return 'generate_code';
}`;
  }

  // Generic template - access properties directly from state root
  const target1 = possibleOutputs[0] || 'target_a';
  const target2 = possibleOutputs[1] || 'target_b';

  return `function route(state) {
  // Access node outputs directly from state
  // Example: state.validation, state.analysis, state.codeImplementation, etc.
  
  if (state.success) {
    return '${target1}';
  }

  return '${target2}';
}`;
}
